import { Injectable } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import {
    debounceTime,
    distinctUntilChanged,
    Observable,
    map,
    startWith,
} from "rxjs";
import isEqual from "lodash/isEqual";

export interface DateRangeFilter {
    startDate: Date | string | null; // Date objects for calendar display, ISO strings for API
    endDate: Date | string | null; // Date objects for calendar display, ISO strings for API
}

export interface HomeFilters {
    dateRange: DateRangeFilter;
    formType: string | null;
    sortOrder: "desc" | "asc"; // Based on your 'timeFrameList' values
    pastDueDate: boolean; // Renamed as per your request
}

@Injectable({
    providedIn: "root",
})
export class HomeFilterService {
    private filterForm = new FormGroup({
        dateRange: new FormGroup({
            startDate: new FormControl<Date | string | null>(null),
            endDate: new FormControl<Date | string | null>(null),
        }),
        formType: new FormControl<string | null>(null), // Explicitly allow null
        // Using nonNullable: true means the control.value will always be 'desc' or 'asc' (never null)
        sortOrder: new FormControl<"desc" | "asc">("asc", {
            nonNullable: true,
        }),
        pastDue: new FormControl<boolean>(false, { nonNullable: true }),
    });

    constructor() {
        // Initialize the date range with "next 7 days"
        const today = new Date();
        const startDate = new Date(today);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);

        this.filterForm.get("dateRange")?.patchValue({
            startDate: startDate, // Store as Date object for calendar display
            endDate: endDate,
        });
    }

    get form(): FormGroup {
        return this.filterForm;
    }

    get valueChanges(): Observable<any> {
        return this.filterForm.valueChanges.pipe(
            debounceTime(200),
            distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        );
    }

    /**
     * Returns an Observable that emits true if a date filter is applied, false otherwise.
     */
    get hasDateFilter$(): Observable<boolean> {
        return this.filterForm.get("dateRange")!.valueChanges.pipe(
            startWith(this.filterForm.get("dateRange")!.value), // Emit initial value
            map((dateRange) => !!dateRange.startDate || !!dateRange.endDate),
        );
    }

    /**
     * Returns the current boolean value indicating if a date filter is applied.
     * Use this when you need an immediate value, not an observable stream.
     */
    get hasDateFilter(): boolean {
        const dateRange = this.filterForm.get("dateRange")!.value;
        return !!dateRange.startDate || !!dateRange.endDate;
    }

    resetFilters(): void {
        // Calculate default "next 7 days" date range
        const today = new Date();
        const startDate = new Date(today);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);

        // Get references to nested form groups/controls
        const dateRangeGroup = this.filterForm.get("dateRange") as FormGroup;

        // Reset the dateRange group to default "next 7 days"
        dateRangeGroup.reset({
            startDate: startDate,
            endDate: endDate,
        });

        // Reset other controls/groups in the main form
        this.filterForm.patchValue({
            // Using patchValue for individual controls/top-level only
            formType: null,
            sortOrder: "asc", // Default sort order: oldest to newest
            pastDue: false,
        });

        // Ensure overall form state is clean (dirty/touched)
        this.filterForm.markAsPristine();
        this.filterForm.markAsUntouched();
    }

    loadPastDueTasks() {
        this.filterForm.patchValue({
            pastDue: true,
            dateRange: {
                startDate: null,
                endDate: null,
            },
        });
    }
}
